import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronDown } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

import gbFlag from '../flags/gb.svg';
import itFlag from '../flags/it.svg';
import esFlag from '../flags/es.svg';
import jaFlag from '../flags/jp.svg';
import deFlag from '../flags/de.svg';
import frFlag from '../flags/fr.svg';

import './Navbar.css';

const LANGUAGES = [
  ['en', gbFlag, 'EN', 'English'],
  ['it', itFlag, 'IT', 'Italiano'],
  ['es', esFlag, 'ES', 'Español'],
  ['ja', jaFlag, 'JA', 'Japanese'],
  ['de', deFlag, 'DE', 'Deutsch'],
  ['fr', frFlag, 'FR', 'Français'],
];

const Navbar = () => {
  const [isOpen,       setIsOpen]       = useState(false);
  const [isMobile,     setIsMobile]     = useState(window.innerWidth <= 1024);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [langOpen,     setLangOpen]     = useState(false);
  const [scrolled,     setScrolled]     = useState(false);

  const panelRef     = useRef(null);
  const hamburgerRef = useRef(null);
  const closeTimers  = useRef({});

  const navigate      = useNavigate();
  const { t, i18n }  = useTranslation('navbar');

  const currentLang = LANGUAGES.find(([l]) => l === i18n.language) ?? LANGUAGES[0];

  /* ── Scroll ─────────────────────────────────────── */
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  /* ── Resize ─────────────────────────────────────── */
  useEffect(() => {
    const fn = () => {
      const m = window.innerWidth <= 1024;
      setIsMobile(m);
      if (!m) closeAll();
    };
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  /* ── Click outside panel ────────────────────────── */
  useEffect(() => {
    const fn = (e) => {
      if (
        isMobile && isOpen &&
        panelRef.current     && !panelRef.current.contains(e.target) &&
        hamburgerRef.current && !hamburgerRef.current.contains(e.target)
      ) closeAll();
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [isMobile, isOpen]);

  /* ── Body scroll lock (iOS-safe) ────────────────── */
  useEffect(() => {
    if (isMobile && isOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [isOpen, isMobile]);

  /* ── Helpers ─────────────────────────────────────── */
  const closeAll    = () => { setIsOpen(false); setOpenDropdown(null); setLangOpen(false); };
  const togglePanel = () => setIsOpen(p => !p);
  const toggleSub   = (name) => setOpenDropdown(p => p === name ? null : name);
  const goTo        = (path) => { navigate(path); closeAll(); };

  /* ── Desktop hover helpers ───────────────────────── */
  const openItem  = (name) => { clearTimeout(closeTimers.current[name]); setOpenDropdown(name); };
  const closeItem = (name) => {
    closeTimers.current[name] = setTimeout(
      () => setOpenDropdown(cur => cur === name ? null : cur),
      90
    );
  };

  /* ── NavItem ─────────────────────────────────────── */
  const NavItem = ({ title, route, name, children, className = '' }) => (
    <div
      className={`nav-item ${openDropdown === name ? 'open' : ''} ${className}`}
      onMouseEnter={() => !isMobile && children && openItem(name)}
      onMouseLeave={() => !isMobile && children && closeItem(name)}
    >
      <button
        className="nav-button"
        onClick={() => {
          if (isMobile) {
            children ? toggleSub(name) : goTo(route);
          } else {
            if (route)         goTo(route);
            else if (children) toggleSub(name);
          }
        }}
      >
        {title}
        {children && (
          <FaChevronDown
            className={`dropdown-icon ${openDropdown === name ? 'rotated' : ''}`}
          />
        )}
      </button>

      {children && (
        <div
          className="dropdown-menu"
          onMouseEnter={() => !isMobile && openItem(name)}
          onMouseLeave={() => !isMobile && closeItem(name)}
        >
          {React.Children.map(children, child =>
            React.cloneElement(child, { onClick: () => goTo(child.props.to) })
          )}
        </div>
      )}
    </div>
  );

  /* ── DropdownItem ────────────────────────────────── */
  const DropdownItem = ({ children, to, onClick }) => (
    <button
      className="dropdown-item"
      onClick={(e) => { onClick?.(e); if (to) goTo(to); }}
    >
      {children}
    </button>
  );

  /* ── Shared nav link tree ────────────────────────── */
  const NavLinks = () => (
    <>
      <button className="nav-button" onClick={() => goTo('/')}>
        {t('home')}
      </button>

      <NavItem title={t('destinations')} route="/destinations" name="destinations">
        <DropdownItem to="/places/lnaivasha">{t('dropdown.lakeNaivasha')}</DropdownItem>
        <DropdownItem to="/places/mara">{t('dropdown.maasaiMara')}</DropdownItem>
        <DropdownItem to="/places/nakuru">{t('dropdown.nakuru')}</DropdownItem>
        <DropdownItem to="/places/amboseli">{t('dropdown.amboseli')}</DropdownItem>
        <DropdownItem to="/places/samburu">{t('dropdown.samburu')}</DropdownItem>
        <DropdownItem to="/places/tsavo">{t('dropdown.tsavo')}</DropdownItem>
        <DropdownItem to="/places/olpejeta">{t('dropdown.olpejeta')}</DropdownItem>
        <DropdownItem to="/places/shela">{t('dropdown.shela')}</DropdownItem>
      </NavItem>

      <NavItem title={t('ourSafaris')} route="/tours" name="tours">
        <DropdownItem to="/Maasai-CultureSafari">Maasai Culture Immersion</DropdownItem>
        <DropdownItem to="/Bush-BeachSafari">{t('dropdown.bushBeach')}</DropdownItem>
        <DropdownItem to="/Osiligi-Clinic-Safari">{t('dropdown.health')}</DropdownItem>
        <DropdownItem to="/Mara-DeepDive">{t('dropdown.maraExclusive')}</DropdownItem>
        <DropdownItem to="/RiftValleyClassic">{t('dropdown.riftValley')}</DropdownItem>
        <DropdownItem to="/Conservation-Safari">{t('dropdown.northernCircuit')}</DropdownItem>
        <DropdownItem to="/Amboseli+Tsavo">{t('dropdown.southernCircuit')}</DropdownItem>
        <DropdownItem to="/Kenya-Classic">{t('dropdown.kenyaClassic')}</DropdownItem>
        <DropdownItem to="/CrossBorderSafari">{t('dropdown.crossBorder')}</DropdownItem>
        <DropdownItem to="/flysafari">{t('dropdown.fly')}</DropdownItem>
        <DropdownItem to="/customizesafari">{t('dropdown.custom')}</DropdownItem>
      </NavItem>

      <button className="nav-button" onClick={() => goTo('/accomodations')}>
        {t('accommodations')}
      </button>

      <NavItem title={t('aboutUs')} route="/about" name="about">
        <DropdownItem to="/about#about">{t('dropdown.company')}</DropdownItem>
        <DropdownItem to="/about#Olenini">{t('dropdown.fleet')}</DropdownItem>
        <DropdownItem to="/gallery">{t('dropdown.gallery')}</DropdownItem>
        <DropdownItem to="/osiligi">{t('dropdown.osiligi')}</DropdownItem>
      </NavItem>

      <button className="nav-button" onClick={() => goTo('/contact')}>
        {t('contactUs')}
      </button>
    </>
  );

  /* ── RENDER ──────────────────────────────────────── */
  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="navbar-container">

        {/* Logo */}
        <div className="navbar-logo" onClick={() => goTo('/')}>
          <img src="/images/OLENINI-logoV2.1.PNG" alt="Olenini Safaris" className="logo-img" />
          <div className="logo-text">
            <span className="logo-name">Olenini</span>
            <span className="logo-tagline">Tours & Safaris</span>
          </div>
        </div>

        {/* Desktop */}
        {!isMobile && (
          <div className="nav-links-desktop">
            <NavLinks />

            <div
              className={`language-selector ${langOpen ? 'open' : ''}`}
              onMouseEnter={() => setLangOpen(true)}
              onMouseLeave={() => setLangOpen(false)}
            >
              <button className="language-button">
                <img src={currentLang[1]} alt={currentLang[3]} className="lang-flag" />
                <span className="lang-code">{currentLang[2]}</span>
                <FaChevronDown className={`lang-arrow ${langOpen ? 'rotated' : ''}`} />
              </button>
              <div className="language-dropdown">
                {LANGUAGES.map(([lang, flag, , label]) => (
                  <button
                    key={lang}
                    className={`language-option ${i18n.language === lang ? 'active' : ''}`}
                    onClick={() => { i18n.changeLanguage(lang); setLangOpen(false); }}
                  >
                    <img src={flag} alt={label} className="lang-flag" />
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Mobile hamburger */}
        {isMobile && (
          <div
            ref={hamburgerRef}
            className={`hamburger ${isOpen ? 'active' : ''}`}
            onClick={togglePanel}
            role="button"
            aria-label="Toggle menu"
            aria-expanded={isOpen}
          >
            <span className="bar" />
            <span className="bar" />
            <span className="bar" />
          </div>
        )}
      </div>

      {/* Mobile panel */}
      {isMobile && (
        <>
          <div
            className={`mobile-overlay ${isOpen ? 'visible' : ''}`}
            onClick={closeAll}
          />

          <div
            ref={panelRef}
            className={`nav-links-mobile ${isOpen ? 'open' : ''}`}
            aria-hidden={!isOpen}
          >
            <div className="mobile-panel-inner">
              <NavLinks />

              <div className="mobile-language">
                <p className="mobile-lang-label">Language</p>
                <div className="mobile-lang-grid">
                  {LANGUAGES.map(([lang, flag, code, label]) => (
                    <button
                      key={lang}
                      className={`mobile-lang-tile ${i18n.language === lang ? 'active' : ''}`}
                      onClick={() => { i18n.changeLanguage(lang); closeAll(); }}
                    >
                      <img src={flag} alt={label} className="lang-flag" />
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mobile-panel-spacer" />
            </div>
          </div>
        </>
      )}
    </nav>
  );
};

export default Navbar;